/**
 * Tab Renderer Registry
 * 
 * Maps TabType to renderer functions. TypeScript enforces all types have an entry.
 * Heavy components stay behind Dynamic() inside each renderer (lazy loading).
 */
import type { TabType } from "@/contexts/tabsContext";
import type { TabData } from "rc-dock";
import { TabTitle } from "@/chat2/components/chatlayout/TabTitle";
import { TabPlaceholder } from "@/chat2/components/chatlayout/TabPlaceholder";
import { WelcomeTabRenderer } from "@/chat2/components/chatlayout/WelcomeTab";
import { WorkspaceTabRenderer } from "@/chat2/components/workspace/WorkspaceTabs";
import { ChangeLogTabRenderer } from "@/chat2/components/tabTypes/ChangeLogTabs";
import {
  AdminLogsTabRenderer,
  AdminPerfAnalyzerTabRenderer,
  AdminUsersTabRenderer,
  AdminUserConsoleTabRenderer,
  AdminRolesTabRenderer,
  AdminModelProfilesTabRenderer,
  AdminProviderProfilesTabRenderer,
  AdminPlansTabRenderer,
  AdminSettingsTabRenderer,
  AdminWorkspaceDefaultsTabRenderer,
  AdminDefaultPromptsTabRenderer,
  AdminWelcomeSettingsTabRenderer,
  AdminFetchSettingsTabRenderer,
  AdminBudgetDefaultsTabRenderer,
  AdminPonderDefaultsTabRenderer,
  AdminMetricsTabRenderer,
} from "@/chat2/components/tabTypes/AdminTabs";
import {
  ProfileTabRenderer,
  SubscriptionTabRenderer,
} from "@/chat2/components/tabTypes/ProfileTabs";
import { IntegrationTabRenderer } from "@/chat2/components/tabTypes/IntegrationTabs";
import { WikiScopeTabRenderer, WikiPageTabRenderer } from "@/components/wiki/wikiTabRenderers";
import { FilePreviewTabRenderer } from "@/components/filePreview/filePreviewTabRenderer";
import { PreviewBrowserTabRenderer } from "@/components/previewBrowser/previewBrowserTabRenderer";

export type TabRenderer = (id: string) => TabData;

/** Fallback renderer for not-yet-implemented tabs */
const NotImplementedRenderer: TabRenderer = (id: string): TabData => ({
  id,
  title: <TabTitle id={id} />,
  content: <TabPlaceholder title="Not Implemented" id={id} />,
});

/**
 * Registry mapping TabType to renderer functions.
 * Record<TabType, TabRenderer> ensures TypeScript enforces all types have an entry.
 */
export const tabRenderers: Record<TabType, TabRenderer> = {
  // Core tabs
  "welcome": WelcomeTabRenderer,
  "workspace": WorkspaceTabRenderer,
  "changelog": ChangeLogTabRenderer,
  
  // Profile tabs
  "profile": ProfileTabRenderer,
  "subscription": SubscriptionTabRenderer,
  
  // Integration tabs
  "integration": IntegrationTabRenderer,
  
  // Admin tabs
  "admin-logs": AdminLogsTabRenderer,
  "admin-perf-analyzer": AdminPerfAnalyzerTabRenderer,
  "admin-users": AdminUsersTabRenderer,
  "admin-user-console": AdminUserConsoleTabRenderer,
  "admin-roles": AdminRolesTabRenderer,
  "admin-model-profiles": AdminModelProfilesTabRenderer,
  "admin-provider-profiles": AdminProviderProfilesTabRenderer,
  "admin-plans": AdminPlansTabRenderer,
  "admin-settings": AdminSettingsTabRenderer,
  "admin-workspace-defaults": AdminWorkspaceDefaultsTabRenderer,
  "admin-default-prompts": AdminDefaultPromptsTabRenderer,
  "admin-welcome-settings": AdminWelcomeSettingsTabRenderer,
  "admin-fetch-settings": AdminFetchSettingsTabRenderer,
  "admin-budget-defaults": AdminBudgetDefaultsTabRenderer,
  "admin-ponder-defaults": AdminPonderDefaultsTabRenderer,
  "admin-metrics": AdminMetricsTabRenderer,
  
  // Wiki tabs
  "wiki-scope": WikiScopeTabRenderer,
  "wiki-page": WikiPageTabRenderer,

  // File preview
  "file-preview": FilePreviewTabRenderer,

  // Preview browser (HTML assets)
  "preview-browser": PreviewBrowserTabRenderer,

  // Not yet implemented
  "assets": NotImplementedRenderer,
  "webpage": NotImplementedRenderer,
};
